/**
 * CompetencySetSection Component
 * Admin section for assigning competency sets to companies
 * Matches the visual pattern of "Assign Unpack Values" and "DiagnosticSection"
 *
 * Features:
 * - View all available competency sets
 * - Assign/Unassign a competency set to the current company
 * - Link to editor to view full competency set details
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useGetCompetencySetsQuery,
  useAssignCompaniesToSetMutation,
  useRemoveCompaniesFromSetMutation,
} from "../../../services/api";
import CompetencySetFormModal from "../CompetencySets/CompetencySetFormModal";

// ============================================
// SVG ICONS
// ============================================
const IconEdit = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/**
 * CompetencySetSection Component
 * @param {string} companyId - Company ID
 */
const CompetencySetSection = ({ companyId }) => {
  const navigate = useNavigate();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // ============================================
  // API HOOKS
  // ============================================
  const { data: setsData, isLoading, refetch } = useGetCompetencySetsQuery({ limit: 100 });
  const competencySets = setsData?.competencySets || [];

  const [assignCompanies, { isLoading: isAssigning }] = useAssignCompaniesToSetMutation();
  const [removeCompanies, { isLoading: isUnassigning }] = useRemoveCompaniesFromSetMutation();

  // ============================================
  // HELPERS
  // ============================================
  // Check if this company is assigned to a given competency set
  const isAssignedToCompany = (set) => {
    return set.companies?.some((c) => {
      const cId = typeof c === 'object' ? (c._id || c.id) : c;
      return cId === companyId;
    });
  };

  // Find the currently assigned set (if any)
  const assignedSet = competencySets.find(isAssignedToCompany);

  // ============================================
  // HANDLERS
  // ============================================
  const handleAssign = async (setId) => {
    try {
      await assignCompanies({ id: setId, companyIds: [companyId] }).unwrap();
      refetch();
    } catch (error) {
      console.error("Failed to assign competency set:", error);
    }
  };

  const handleUnassign = async (setId) => {
    try {
      await removeCompanies({ id: setId, companyIds: [companyId] }).unwrap();
      refetch();
    } catch (error) {
      console.error("Failed to unassign competency set:", error);
    }
  };

  const handleViewEdit = (setId) => {
    navigate(`/admin/competency-sets/${setId}`, {
      state: {
        from: `/admin/companies/${companyId}/competencies`,
        fromLabel: "Company Competencies",
      },
    });
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <>
    <div className="cd-card-block cd-no-padding">
      <div className="cd-section-header-simple">
        <div className="cd-section-title-main">Assign Competency Set</div>
        <button
          className="cd-add-btn"
          onClick={() => setIsCreateModalOpen(true)}
        >
          + Add New
        </button>
      </div>

      <div className="cd-section-subtitle">
        Assign a competency set to this company. Only one set can be active at a time.
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="cd-loading">Loading competency sets...</div>
      )}

      {/* Empty State */}
      {!isLoading && competencySets.length === 0 && (
        <div className="cd-empty-state">
          <p>No competency sets available. Create one using "+ Add New".</p>
        </div>
      )}

      {/* Competency Sets Grid */}
      {!isLoading && competencySets.length > 0 && (
        <div className="cd-diagnostic-grid">
          {competencySets.map((set) => {
            const isAssigned = isAssignedToCompany(set);
            const companiesCount = set.companies?.length || 0;

            return (
              <div
                key={set._id}
                className={`cd-diagnostic-card ${isAssigned ? "cd-card-assigned" : ""}`}
              >
                {/* Assigned Badge */}
                {isAssigned && (
                  <div className="cd-assigned-badge">
                    <IconCheck /> Assigned
                  </div>
                )}

                {/* Edit Icon */}
                <div className="cd-card-icons">
                  <button
                    className="cd-icon-btn cd-edit-icon"
                    onClick={() => handleViewEdit(set._id)}
                    title="View/Edit"
                  >
                    <IconEdit />
                  </button>
                </div>

                {/* Title */}
                <div className="cd-diagnostic-head">
                  <div className="cd-diagnostic-title">{set.name}</div>
                </div>

                {/* Description */}
                <div className="cd-diagnostic-desc">
                  {set.description || "No description provided."}
                </div>

                {/* Meta Info */}
                <div className="cd-diagnostic-meta">
                  <span className="cd-template-status" data-status={set.isActive ? "published" : "draft"}>
                    {set.isActive ? "Active" : "Inactive"}
                  </span>
                  <span className="cd-assignment-count">
                    {companiesCount} {companiesCount === 1 ? "company" : "companies"}
                  </span>
                </div>

                {/* Action Buttons */}
                <div className="cd-diagnostic-actions">
                  {isAssigned ? (
                    <button
                      className="cd-assign-btn cd-unassign-btn"
                      onClick={() => handleUnassign(set._id)}
                      disabled={isUnassigning}
                    >
                      Unassign
                    </button>
                  ) : (
                    <button
                      className="cd-assign-btn"
                      onClick={() => handleAssign(set._id)}
                      disabled={isAssigning || (assignedSet && assignedSet._id !== set._id)}
                      title={assignedSet ? "Unassign current set first" : ""}
                    >
                      Assign →
                    </button>
                  )}
                  <button
                    className="cd-open-btn"
                    onClick={() => handleViewEdit(set._id)}
                  >
                    View/Edit →
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>

    {/* Create Modal */}
    {isCreateModalOpen && (
      <CompetencySetFormModal
        competencySet={null}
        onClose={() => {
          setIsCreateModalOpen(false);
          refetch();
        }}
      />
    )}
    </>
  );
};

export default CompetencySetSection;
